import clsx from 'clsx'
import {useFormik} from 'formik'
import {find, first, isEmpty, uniqBy} from 'lodash'
import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {Modal} from 'react-bootstrap'
import Container from 'react-bootstrap/Container'
import {useIntl} from 'react-intl'
import {toast} from 'react-toastify'
import * as Yup from 'yup'
import HttpStatusCode from '../../../../api/HttpStatusCode'
import {A1_CONSTANTS, A1BatchService} from '../../../a1'
import {ConfirmActionModal, KTSVG} from '../../../../../gori/components'
import {useAuth} from '../../../../../gori/providers'
import {useCancelToken, UseCancelTokenHasKey, UseYupValidate} from '../../../../../gori/hooks'
import {
  Button,
  InputTextFormik,
  SelectFormik,
  ValidationErrorModal,
} from '../../../../../gori/widgets'
import {FileErrorModal} from '../../../../../gori/widgets/modals/FileErrorModal'
import {A1ShipFromOrderModal} from './A1ShipFromOrderModal'
import {A1BatchDuplicateModal} from './A1BatchDuplicateModal'

type Props = {
  show: boolean
  handleClose: () => void
  orders?: any
  file?: any
  reloadTable: () => any
}

type OrderShortInfo = {
  id: string | number
  a1_batch_id?: string | number | null
  from: {
    city?: string
    state?: string
    country?: string
    zip?: string
    company?: string
    name?: string
    phone?: string
    email?: string
    street1?: string
    street2?: string
  }
  fromString: string
  status: string
}

const AddToA1BatchModal: React.FC<Props> = ({
  show,
  handleClose,
  orders,
  file = null,
  reloadTable,
}) => {
  const intl = useIntl()
  const {loadingSwitch, currentUser} = useAuth()
  const {stringYup} = UseYupValidate()
  const {newCancelToken, isCancel} = useCancelToken()
  const {newCancelTokenHasKey, isCancelHasKey} = UseCancelTokenHasKey()
  const [loading, setLoading] = useState<{first: boolean; submit: boolean}>({
    first: true,
    submit: false,
  })
  const [hasShow, setHasShow] = useState<{
    smart: boolean
    create: boolean
    existing: boolean
    batchesDuplicate: boolean
    fromAddressOrders: boolean
    confirmAction: boolean
    validateFile: boolean
  }>({
    smart: false,
    create: false,
    existing: false,
    batchesDuplicate: false,
    fromAddressOrders: false,
    confirmAction: false,
    validateFile: false,
  })
  const [dataModal, setDataModal] = useState<{
    batchesDuplicate: any
    fromAddressOrders: any
    confirmAction: string
    validateFile: any
  }>({
    batchesDuplicate: [],
    fromAddressOrders: [],
    confirmAction: '',
    validateFile: [],
  })
  const [hasPass, setHasPass] = useState<{shipFrom: boolean}>({shipFrom: false})
  const [validationErrors, setValidationErrors] = useState<any>()
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [progress, setProgress] = useState(0)
  const [optionsBatches, setOptionsBatches] = useState<any>([])

  // BEGIN:  fake progress
  // setProgress(0)
  // const fakeProgressInterval = setInterval(() => {
  //   setProgress((prevProgress) => {
  //     if (prevProgress < 80) {
  //       return prevProgress + 10
  //     }
  //     return prevProgress
  //   })
  // }, 500)

  // clearInterval(fakeProgressInterval)
  // setProgress(100)
  // END:  fake progress

  const initValidateSchema = useMemo(() => {
    return {
      active_box: Yup.string()
        .nullable()
        .oneOf(
          [...Object.values(A1_CONSTANTS.STATUS_ADD_TO_A1_BATCH.ACTIVE_BOX), 'null'],
          intl.formatMessage({id: 'INVALID_OPTION'})
        )
        .required(intl.formatMessage({id: 'PLEASE_SELECT_AT_LEAST_ONE_OPTION'})),
      smart: Yup.string()
        .nullable()
        .when('active_box', {
          is: A1_CONSTANTS.STATUS_ADD_TO_A1_BATCH.ACTIVE_BOX.SMART,
          then: stringYup(255, 'SMART_A1_BATCH'),
        }),
      create: Yup.string()
        .nullable()
        .when('active_box', {
          is: A1_CONSTANTS.STATUS_ADD_TO_A1_BATCH.ACTIVE_BOX.CREATE,
          then: stringYup(255, 'A1_BATCH_NAME'),
        }),
      existing: Yup.string()
        .nullable()
        .when('active_box', {
          is: A1_CONSTANTS.STATUS_ADD_TO_A1_BATCH.ACTIVE_BOX.EXISTING,
          then: stringYup(255, 'SELECT_A_A1_BATCH'),
        }),
    }
  }, [intl, stringYup])

  const shipFromToString = useCallback(
    (from: Record<string, any>) =>
      Object.keys(from)
        .sort()
        .map((k) => {
          const v = from[k]
          return typeof v === 'string' ? v.trim() : String(v || '')
        })
        .join('|'),
    []
  )

  const ordersShortInfo: OrderShortInfo[] = useMemo(() => {
    return (
      orders?.map((item) => {
        const from = {
          company: item.original.from_company || '',
          first_name: item.original.from_first_name || '',
          last_name: item.original.from_last_name || '',
          street_1: item.original.from_street1 || '',
          street_2: item.original.from_street2 || '',
          city: item.original.from_city || '',
          state: item.original.from_state || '',
          country: item.original.from_country || '',
          zip: item.original.from_zip || '',
          phone: item.original.from_phone || '',
          email: item.original.from_email || '',
        }

        return {
          id: item.original.id,
          a1_batch_id: item.original.a1_batch_id,
          from,
          fromString: shipFromToString(from),
          status: item.original.status,
        }
      }) || []
    )
  }, [orders, shipFromToString])

  const handleFileUpload = async (values) => {
    if (!file) return

    const isCreateBox = values.active_box === A1_CONSTANTS.STATUS_ADD_TO_A1_BATCH.ACTIVE_BOX.CREATE
    const isExistingBox =
      values.active_box === A1_CONSTANTS.STATUS_ADD_TO_A1_BATCH.ACTIVE_BOX.EXISTING

    const createPayload = () => {
      const payload = new FormData()
      payload.append('file', file)
      payload.append('type', 'create')

      if (isCreateBox) {
        payload.append('name', values.create || '')
      } else if (isExistingBox) {
        payload.append('existing_batch', values.existing || '')
      }

      return payload
    }
    const payload = createPayload()

    const showConfirmModal = (messageKey: string) => {
      setDataModal((prev) => ({
        ...prev,
        confirmAction: messageKey,
      }))
      setHasShow((prev) => ({...prev, confirmAction: true}))
    }

    if (!hasPass.shipFrom) {
      if (isCreateBox) {
        const from = await checkUniqueAddressesInBatch(payload)
        if (!isEmpty(from) && from?.length !== 1) {
          return showConfirmModal('THE_1ST_RECORD_WILL_OVERWRITE')
        }
      } else if (isExistingBox) {
        const from = await checkOrdersInBatch(values.existing)
        return showConfirmModal(
          isEmpty(from)
            ? 'THE_1ST_RECORD_WILL_OVERWRITE'
            : 'THE_FROM_ADDRESS_OF_SELECTED_A1_BATCH_WILL_OVERWRITE'
        )
      }
    }

    const config = {
      headers: {'content-type': 'multipart/form-data'},
      cancelToken: newCancelToken(),
    }

    try {
      setLoading((prev) => ({...prev, submit: true}))
      const res = await A1BatchService.store(payload, config)

      if (res) {
        toast.success(
          intl.formatMessage({
            id: isCreateBox ? 'CREATE_LABEL_SUCCESS_AND_WAITING' : 'ADDED_TO_A1_BATCH_SUCCESSFULLY',
          })
        )
        reloadTable()
        handleClose()
      }
    } catch (error: any) {
      if (isCancel(error)) return
      if (error?.response?.status === HttpStatusCode.UNPROCESSABLE_ENTITY) {
        setDataModal((prev) => ({...prev, validateFile: error?.response?.data?.errors?.fields}))
        setHasShow((prev) => ({...prev, validateFile: true}))
      } else {
        setValidationErrors(error?.response)
      }
    } finally {
      setLoading((prev) => ({...prev, submit: false}))
    }
  }

  const handleAddOrderManually = async (values) => {
    const isCreateBox = values.active_box === A1_CONSTANTS.STATUS_ADD_TO_A1_BATCH.ACTIVE_BOX.CREATE
    const isExistingBox =
      values.active_box === A1_CONSTANTS.STATUS_ADD_TO_A1_BATCH.ACTIVE_BOX.EXISTING

    const shippedOrders = ordersShortInfo.filter((o) => o.status === 'shipped')
    const openOrders = ordersShortInfo.filter((o) => o.status === 'open')

    const uniqueShipFromOpenOrders = uniqBy(openOrders, 'fromString')
    const uniqueShipFromShippedOrders = uniqBy(shippedOrders, 'fromString')

    if (!hasPass.shipFrom) {
      if (isCreateBox) {
        const ok = addToNewBatch(openOrders, shippedOrders, values)
        if (!ok) return
      }

      if (isExistingBox) {
        const selectedBatch = optionsBatches.find((item) => item.value === values.existing)

        if (!selectedBatch.order_count) {
          const ok = addToNewBatch(openOrders, shippedOrders, values)
          if (!ok) return
        } else if (!shippedOrders.length) {
          if (
            uniqueShipFromOpenOrders.length > 1 ||
            uniqueShipFromOpenOrders.some(
              (o) => o.fromString !== shipFromToString(selectedBatch.ship_from)
            )
          ) {
            values.shipFrom = selectedBatch.from
            return showModal('confirmAction', 'CURRENT_A1_BATCH_WILL_OVERWRITE_SELECTED_ORDERS')
          }
          values.shipFrom = selectedBatch.from
          setHasPass((prev) => ({...prev, shipFrom: true}))
        } else if (uniqueShipFromShippedOrders.length > 1) {
          return setValidationErrors({
            data: {
              error: {
                message: intl.formatMessage({
                  id: 'ALL_SHIPMENTS_IN_A_BATCH_MUST_HAVE_THE_SAME_FROM_ADDRESS',
                }),
              },
            },
          })
        } else if (shippedOrders[0].fromString !== shipFromToString(selectedBatch.ship_from)) {
          return setValidationErrors({
            data: {
              error: {
                message: intl.formatMessage({
                  id: 'ALL_SHIPMENTS_IN_A_BATCH_MUST_HAVE_THE_SAME_FROM_ADDRESS',
                }),
              },
            },
          })
        }
      }
    }

    const orderChecked = orders.reduce(
      (acc: any, order: any) => {
        const key = order.original.a1_batch_id ? 'hasBatch' : 'noBatch'
        acc[key].push(order.original.id)
        return acc
      },
      {hasBatch: [], noBatch: []}
    )

    const payload = {
      type: A1_CONSTANTS.STATUS_ADD_TO_A1_BATCH.ACTIVE_BOX.CREATE,
      has_batch: orderChecked.hasBatch,
      no_batch: orderChecked.noBatch,
      from: values.shipFrom,
      ...(isCreateBox ? {name: values.create} : {existing_batch: values.existing}),
    }

    if (orderChecked.hasBatch.length > 0) {
      showModal('batchesDuplicate', {
        payload,
        name_position:
          values.active_box === A1_CONSTANTS.STATUS_ADD_TO_A1_BATCH.ACTIVE_BOX.EXISTING
            ? find(optionsBatches, {value: values.existing}).label
            : values.create,
      })
    } else {
      const config = {cancelToken: newCancelToken()}

      try {
        setLoading((prev) => ({...prev, submit: true}))
        const res = await A1BatchService.store(payload, config)

        if (res) {
          toast.success(intl.formatMessage({id: 'ADDED_TO_A1_BATCH_SUCCESSFULLY'}))
          reloadTable()
          handleClose()
        }
      } catch (error: any) {
        if (isCancel(error)) return
        setValidationErrors(error?.response)
      } finally {
        setLoading((prev) => ({...prev, submit: false}))
      }
    }
  }

  const addToNewBatch = (openOrders: OrderShortInfo[], shippedOrders: OrderShortInfo[], values) => {
    const uniqueShipFromOpenOrders = uniqBy(openOrders, 'fromString')
    const uniqueShipFromShippedOrders = uniqBy(shippedOrders, 'fromString')

    if (!shippedOrders.length && openOrders.length) {
      if (uniqueShipFromOpenOrders.length > 1) {
        showModal('fromAddressOrders', {orderIds: ordersShortInfo.map((o) => o.id)})
        return false
      }
      values.shipFrom = uniqueShipFromOpenOrders[0].from
      setHasPass((prev) => ({...prev, shipFrom: true}))
      return true
    }

    if (shippedOrders.length && !openOrders.length) {
      if (uniqueShipFromShippedOrders.length > 1) {
        setValidationErrors({
          data: {
            error: {
              message: intl.formatMessage({
                id: 'ALL_SHIPMENTS_IN_A_BATCH_MUST_HAVE_THE_SAME_FROM_ADDRESS',
              }),
            },
          },
        })
        return false
      }
      values.shipFrom = uniqueShipFromShippedOrders[0].from
      setHasPass((prev) => ({...prev, shipFrom: true}))
      return true
    }

    if (shippedOrders.length && openOrders.length) {
      if (uniqueShipFromShippedOrders.length > 1) {
        setValidationErrors({
          data: {
            error: {
              message: intl.formatMessage({
                id: 'ALL_SHIPMENTS_IN_A_BATCH_MUST_HAVE_THE_SAME_FROM_ADDRESS',
              }),
            },
          },
        })
        return false
      }

      if (openOrders.every((o) => o.fromString === uniqueShipFromShippedOrders[0].fromString)) {
        values.shipFrom = uniqueShipFromShippedOrders[0].from
        setHasPass((prev) => ({...prev, shipFrom: true}))
        return true
      }
      values.shipFrom = uniqueShipFromShippedOrders[0].from
      showModal(
        'confirmAction',
        'ORDERS_LABEL_OVERRIDE_SHIP_FROM_ADDRESS_FOR_ORDERS_WITHOUT_LABELS'
      )
      return false
    }
    return true
  }

  const checkOrdersInBatch = useCallback(
    async (batchId) => {
      setLoading((prev) => ({...prev, submit: true}))
      try {
        const {from} = await A1BatchService.getDetail(batchId, {
          cancelToken: newCancelTokenHasKey('check_orders_in_batch'),
        })
        return from
      } catch (error) {
        if (isCancelHasKey(error)) return []
        return []
      } finally {
        setLoading((prev) => ({...prev, submit: false}))
      }
    },
    [isCancelHasKey, newCancelTokenHasKey]
  )

  const showModal = (type: string, data: any) => {
    setDataModal((prev) => ({...prev, [type]: data}))
    setHasShow((prev) => ({...prev, [type]: true}))
  }

  const checkUniqueAddressesInBatch = useCallback(
    async (payload) => {
      setLoading((prev) => ({...prev, submit: true}))
      try {
        const config = {
          headers: {'content-type': 'multipart/form-data'},
          cancelToken: newCancelTokenHasKey('check_unique_addresses_in_batch'),
        }
        const res = await A1BatchService.uniqueAddresses(payload, config)
        const from = res?.from ?? res?.data?.from
        return from
      } catch (error) {
        if (isCancelHasKey(error)) return []
        return []
      } finally {
        setLoading((prev) => ({...prev, submit: false}))
      }
    },
    [isCancelHasKey, newCancelTokenHasKey]
  )

  const formik = useFormik({
    initialValues: {
      active_box: null,
      smart: null,
      create: null,
      existing: null,
      shipFrom: null,
    },
    enableReinitialize: true,
    validationSchema: Yup.object().shape(initValidateSchema),
    onSubmit: async (values) => {
      if (file) {
        await handleFileUpload(values)
      } else {
        await handleAddOrderManually(values)
      }
    },
  })

  const getBatches = useCallback(async () => {
    const config = {
      params: {
        all: true,
        status: A1_CONSTANTS.A1_BATCH_STATUS.OPEN,
      },
      cancelToken: newCancelToken(),
    }
    try {
      const response: any = await A1BatchService.getList(config)
      const a1_batch: any = first(response?.batches || [])
      const prefix = currentUser?.code ? `${currentUser.code}_` : ''
      let nameBatch = prefix + (a1_batch?.id ? a1_batch?.id + 100001 : 100001).toString()

      if (file) {
        nameBatch = `${nameBatch} ${file.name.replace(/(\.csv)+$/, '')}`
      }

      formik.setFieldValue('create', nameBatch)

      setOptionsBatches(
        response?.batches?.map((item) => ({
          label: item.name,
          value: item.id,
        }))
      )
    } catch (error) {
      if (isCancel(error)) return
    } finally {
      setLoading((prev) => ({...prev, first: false}))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCancel, newCancelToken, currentUser?.code])

  useEffect(() => {
    if (loadingSwitch) return
    getBatches()

    return () => {}
  }, [getBatches, loadingSwitch])

  // BEGIN: Action Duplicate Modal
  const handleDuplicateClose = () => {
    setHasShow((prev) => ({...prev, batchesDuplicate: false}))
    handleClose()
  }

  const handleDuplicateSuccess = () => {
    reloadTable()
    handleClose()
  }
  // END: Action Duplicate Modal

  // BEGIN: Action Ship From Modal
  const handleShipFromSubmit = (value) => {
    formik.setFieldValue('shipFrom', value)
    setHasPass((prev) => ({...prev, shipFrom: true}))
    formik.handleSubmit()
  }

  const handleShipFromClose = () => {
    setDataModal((prev) => ({...prev, fromAddressOrders: []}))
    setHasShow((prev) => ({...prev, fromAddressOrders: false}))
  }
  // END: Action Ship From Modal

  // BEGIN: Action Confirm Modal
  const contentConfirmAction = useMemo(() => {
    return (
      <div className='fw-bold'>
        <div className='d-flex flex-column align-items-center justify-content-center'>
          <div>
            {intl.formatMessage({id: 'ALL_SHIPMENTS_IN_A_BATCH_MUST_HAVE_THE_SAME_FROM_ADDRESS'})}
          </div>
          <div className='mt-2 text-center'>
            {dataModal?.confirmAction && intl.formatMessage({id: dataModal?.confirmAction})}
          </div>
        </div>
      </div>
    )
  }, [dataModal.confirmAction, intl])

  const handleConfirmPrimaryAction = () => {
    setHasPass((prev) => ({...prev, shipFrom: true}))
    setHasShow((prev) => ({...prev, confirmAction: false}))
    formik.handleSubmit()
  }

  const handleConfirmSecondaryAction = () => {
    setHasPass((prev) => ({...prev, shipFrom: false}))
    setHasShow((prev) => ({...prev, confirmAction: false}))
  }

  const handleConfirmClose = () => {
    setHasPass((prev) => ({...prev, shipFrom: false}))
    setHasShow((prev) => ({...prev, confirmAction: false}))
  }
  // END: Action Confirm Modal

  return (
    <>
      {validationErrors && (
        <ValidationErrorModal
          handleClose={() => {
            setValidationErrors(undefined)
          }}
          response={validationErrors}
        />
      )}
      {hasShow.validateFile && (
        <FileErrorModal
          show={hasShow.validateFile}
          handleClose={() => setHasShow((prev) => ({...prev, validateFile: false}))}
          errors={dataModal.validateFile}
        />
      )}
      {hasShow.batchesDuplicate && (
        <A1BatchDuplicateModal
          show={hasShow.batchesDuplicate}
          data={dataModal.batchesDuplicate}
          handleClose={handleDuplicateClose}
          handleSuccess={handleDuplicateSuccess}
        />
      )}
      {hasShow.fromAddressOrders && (
        <A1ShipFromOrderModal
          show={hasShow.fromAddressOrders}
          data={dataModal.fromAddressOrders}
          handleSubmit={handleShipFromSubmit}
          handleClose={handleShipFromClose}
        />
      )}
      {hasShow.confirmAction && (
        <ConfirmActionModal
          show={hasShow.confirmAction}
          title={intl.formatMessage({id: 'SHIPMENT_ADDRESS_VALIDATION'})}
          message={contentConfirmAction}
          messagePrimary={intl.formatMessage({id: 'SUBMIT'})}
          messageSecondary={intl.formatMessage({id: 'CANCEL'})}
          handlePrimaryAction={handleConfirmPrimaryAction}
          handleSecondaryAction={handleConfirmSecondaryAction}
          handleClose={handleConfirmClose}
        />
      )}
      <Modal
        id='gori_modal_add_to_A1_batch_modal'
        tabIndex={-1}
        aria-hidden='true'
        centered
        dialogClassName='mw-800px h-auto'
        show={show && !hasShow.batchesDuplicate}
        backdrop='static'
        onHide={() => handleClose()}
      >
        <div className='modal-content'>
          <Modal.Header closeButton>
            <Modal.Title bsPrefix={'fw-bolder fs-1'}>
              {intl.formatMessage({id: 'ADD_TO_A1_BATCH'})}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Container>
              {loading.first ? (
                Array.from({length: 3}).map((item, index) => (
                  <div
                    className='d-flex flex-column border rounded mb-6 p-6 border-secondary cursor-no-drop'
                    key={index}
                  >
                    <div className='fs-2 col-4 mb-3 placeholder placeholder-lg rounded-2 bg-secondary' />
                    <div className='fs-5 col-8 placeholder placeholder-lg rounded-2 bg-secondary' />
                  </div>
                ))
              ) : (
                <div>
                  <div
                    onClick={() => {
                      !loading.submit &&
                        formik.setFieldValue(
                          'active_box',
                          A1_CONSTANTS.STATUS_ADD_TO_A1_BATCH.ACTIVE_BOX.SMART
                        )
                    }}
                    className={clsx('border rounded mb-6 p-6', {
                      'border-secondary':
                        formik.values.active_box !==
                        A1_CONSTANTS.STATUS_ADD_TO_A1_BATCH.ACTIVE_BOX.SMART,
                      'opacity-50':
                        formik.values.active_box &&
                        formik.values.active_box !==
                          A1_CONSTANTS.STATUS_ADD_TO_A1_BATCH.ACTIVE_BOX.SMART,
                      'border-primary':
                        formik.values.active_box ===
                        A1_CONSTANTS.STATUS_ADD_TO_A1_BATCH.ACTIVE_BOX.SMART,
                      'opacity-50 pe-none': true,
                    })}
                  >
                    <div>
                      <h3 className='fw-bolder fs-2 d-flex align-items-center mb-3'>
                        {intl.formatMessage({id: 'SMART_A1_BATCH'})}
                        <KTSVG
                          path='/media/gori/a1/verify.svg'
                          className={clsx('svg-icon svg-icon-1 ms-2', {
                            'text-primary':
                              formik.values.active_box ===
                              A1_CONSTANTS.STATUS_ADD_TO_A1_BATCH.ACTIVE_BOX.SMART,
                          })}
                        />
                      </h3>
                      <span
                        className={clsx('', {
                          'text-gori fw-bolder':
                            formik.values.active_box ===
                            A1_CONSTANTS.STATUS_ADD_TO_A1_BATCH.ACTIVE_BOX.SMART,
                        })}
                      >
                        {intl.formatMessage({id: 'GIVE_A_SUGGESTION_USING_GORI_AI_OPTIMIZATION'})}
                      </span>
                    </div>
                    {formik.values.active_box ===
                      A1_CONSTANTS.STATUS_ADD_TO_A1_BATCH.ACTIVE_BOX.SMART && (
                      <div className='progress h-6px mt-6'>
                        <div
                          role='progressbar'
                          className='progress-bar bg-image-gori'
                          style={{width: `${progress}%`}}
                          aria-valuenow={progress}
                          aria-valuemin={0}
                          aria-valuemax={100}
                        ></div>
                      </div>
                    )}
                  </div>
                  <div
                    onClick={() => {
                      !loading.submit &&
                        formik.setFieldValue(
                          'active_box',
                          A1_CONSTANTS.STATUS_ADD_TO_A1_BATCH.ACTIVE_BOX.CREATE
                        )
                    }}
                    className={clsx('border rounded mb-6 p-6 cursor-pointer', {
                      'border-secondary':
                        formik.values.active_box !==
                        A1_CONSTANTS.STATUS_ADD_TO_A1_BATCH.ACTIVE_BOX.CREATE,
                      'opacity-50':
                        formik.values.active_box &&
                        formik.values.active_box !==
                          A1_CONSTANTS.STATUS_ADD_TO_A1_BATCH.ACTIVE_BOX.CREATE,
                      'border-primary':
                        formik.values.active_box ===
                        A1_CONSTANTS.STATUS_ADD_TO_A1_BATCH.ACTIVE_BOX.CREATE,
                    })}
                  >
                    <div>
                      <h3 className='fw-bolder fs-2 mb-3'>
                        {intl.formatMessage({id: 'CREATE_NEW_A1_BATCH'})}
                      </h3>
                      <span className=''>
                        {intl.formatMessage({id: 'GIVE_A_UNIQUE_NAME_FOR_YOUR_A1_BATCH'})}
                      </span>
                    </div>
                    {formik.values.active_box ===
                      A1_CONSTANTS.STATUS_ADD_TO_A1_BATCH.ACTIVE_BOX.CREATE && (
                      <div className='mt-6'>
                        <InputTextFormik
                          label={intl.formatMessage({id: 'A1_BATCH_NAME'})}
                          formik={formik}
                          name={A1_CONSTANTS.STATUS_ADD_TO_A1_BATCH.ACTIVE_BOX.CREATE}
                          placeholder='100000'
                          disabled={loading.submit}
                          required
                        />
                      </div>
                    )}
                  </div>
                  <div
                    onClick={() => {
                      !loading.submit &&
                        formik.setFieldValue(
                          'active_box',
                          A1_CONSTANTS.STATUS_ADD_TO_A1_BATCH.ACTIVE_BOX.EXISTING
                        )
                    }}
                    className={clsx('border rounded mb-6 p-6 cursor-pointer', {
                      'border-secondary':
                        formik.values.active_box !==
                        A1_CONSTANTS.STATUS_ADD_TO_A1_BATCH.ACTIVE_BOX.EXISTING,
                      'opacity-50':
                        formik.values.active_box &&
                        formik.values.active_box !==
                          A1_CONSTANTS.STATUS_ADD_TO_A1_BATCH.ACTIVE_BOX.EXISTING,
                      'border-primary':
                        formik.values.active_box ===
                        A1_CONSTANTS.STATUS_ADD_TO_A1_BATCH.ACTIVE_BOX.EXISTING,
                    })}
                  >
                    <div>
                      <h3 className='fw-bolder fs-2 mb-3'>
                        {intl.formatMessage({id: 'ADD_TO_EXISTING_A1_BATCH'})}
                      </h3>
                      <span className=''>
                        {intl.formatMessage({id: 'SELECT_A_A1_BATCH_TO_ADD_YOUR_ORDER_TO'})}
                      </span>
                    </div>
                    {formik.values.active_box ===
                      A1_CONSTANTS.STATUS_ADD_TO_A1_BATCH.ACTIVE_BOX.EXISTING && (
                      <div className='mt-6'>
                        <SelectFormik
                          label={intl.formatMessage({id: 'SELECT_A_A1_BATCH'})}
                          placeholder={intl.formatMessage({id: 'A1_BATCH'})}
                          formik={formik}
                          name={A1_CONSTANTS.STATUS_ADD_TO_A1_BATCH.ACTIVE_BOX.EXISTING}
                          options={optionsBatches}
                          disabled={loading.submit}
                          emptyDefault={false}
                          required
                        />
                      </div>
                    )}
                  </div>
                  {formik.errors.active_box && formik.touched.active_box && (
                    <div className='mt-4 text-danger text-center'>{formik.errors.active_box}</div>
                  )}
                </div>
              )}
            </Container>
          </Modal.Body>
          <Modal.Footer>
            <div className='d-flex justify-content-end'>
              <Button
                className='btn btn-secondary me-3'
                label={intl.formatMessage({id: 'CANCEL'})}
                event={() => handleClose()}
                disabled={loading.submit}
              />
              <Button
                className={'btn btn-primary'}
                label={intl.formatMessage({id: 'SUBMIT'})}
                loadingText={intl.formatMessage({id: 'SUBMIT'})}
                disabled={loading.first || loading.submit}
                loading={loading.submit}
                event={formik.handleSubmit}
              />
            </div>
          </Modal.Footer>
        </div>
      </Modal>
    </>
  )
}

export {AddToA1BatchModal}
